import React, { Suspense, useEffect, useRef, useState } from "react";
import {
    AppBar,
    Box,
    Divider,
    Drawer,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tab,
    Tabs,
    Toolbar as ToolbarMui,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import ReduceCapacityOutlinedIcon from "@mui/icons-material/ReduceCapacityOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";

import AliceCarousel from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";

import LoadingSplash from "./commoncComponent/LoadingSplash";
import { TabPanel } from "./TabPanelComponent";
import { BasketComponent } from "./BasketComponent";
import { BottomMenu } from "./HookUtils";

import AvatarIcon from "../assets/icons/avatar.svg";
import { AVATAR_BASE_URL_API, BANNER_BASE_URL_API, BASE_URL_API } from "../Const";
import { CardShopRepository } from "../Repository/CardShopRepository";

const drawerWidth = 240;

// ✅ چون MarketComponent به صورت named export هست، این شکلی lazyش می‌کنیم
const LazyMarketComponent = React.lazy(() =>
    import("./MarketComponent").then((module) => ({
        default: module.MarketComponent,
    }))
);

const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
}));

function ToolbarComponent() {
    const [fetchedCategories, setFetchedCategories] = useState([]);
    const [userData, setUserData] = useState(null);
    const [banners, setBanners] = useState([]);
    const [value, setValue] = useState(0);
    const [open, setOpen] = useState(false);
    const [loadingVisible, setLoadingVisible] = useState(true);
    const [cardShopCount, setCardShopCount] = useState(0); // اگر جایی استفاده بشه، نگهش داشتیم

    const loadingRef = useRef(null);
    const tabsRef = useRef(null);

    useEffect(() => {
        const token = localStorage.getItem("pwa_token");
        const headers = {
            Authorization: token ? "Bearer " + token : "",
            "Content-Type": "application/json",
        };

        // Splash حداقل 900ms دیده بشه
        loadingRef.current?.show("درحال بارگزاری فروشگاه ها...");
        const startedAt = Date.now();
        const minSplash = 900;

        setCardShopCount(CardShopRepository.getAll()?.length || 0);

        const pCats = fetch(BASE_URL_API + "category", { headers })
            .then((r) => r.json())
            .then((res) => setFetchedCategories(res?.data || []))
            .catch(console.error);

        const pBanners = fetch(BASE_URL_API + "banner?type=home")
            .then((r) => r.json())
            .then((res) => setBanners(res?.data || []))
            .catch(console.error);

        const pUser = token
            ? fetch(BASE_URL_API + "user", { headers })
                .then((r) => r.json())
                .then((res) => {
                    if (res?.code === "200") setUserData(res.data);
                })
                .catch(console.error)
            : Promise.resolve();

        Promise.all([pCats, pBanners, pUser]).finally(() => {
            const elapsed = Date.now() - startedAt;
            const delay = Math.max(0, minSplash - elapsed);
            setTimeout(() => {
                loadingRef.current?.hide();
                setLoadingVisible(false);
            }, delay);
        });
    }, []);

    useEffect(() => {
        // 🚨 Wait until loading is finished and the Tabs exist in the DOM
        if (loadingVisible) return;

        // 1. Get the actual DOM node of the tabs
        const tabsNode = tabsRef.current;
        if (!tabsNode) return;

        // 2. Find the internal MUI scroller element
        const scroller = tabsNode.querySelector('.MuiTabs-scroller');
        if (!scroller) return;

        let isDown = false;
        let startX;
        let scrollLeft;

        const onMouseDown = (e) => {
            isDown = true;
            // Change cursor to show dragging state
            scroller.style.cursor = 'grabbing';
            startX = e.pageX - scroller.offsetLeft;
            scrollLeft = scroller.scrollLeft;
        };

        const onMouseLeave = () => {
            isDown = false;
            scroller.style.cursor = 'grab'; // Revert to grab instead of default
        };

        const onMouseUp = () => {
            isDown = false;
            scroller.style.cursor = 'grab'; // Revert to grab instead of default
        };

        const onMouseMove = (e) => {
            if (!isDown) return;
            e.preventDefault(); // Prevent text selection
            const x = e.pageX - scroller.offsetLeft;
            const walk = (x - startX) * 1.5; // Multiply by 1.5 to scroll faster
            scroller.scrollLeft = scrollLeft - walk;
        };

        // 3. Attach the event listeners
        scroller.addEventListener('mousedown', onMouseDown);
        scroller.addEventListener('mouseleave', onMouseLeave);
        scroller.addEventListener('mouseup', onMouseUp);
        scroller.addEventListener('mousemove', onMouseMove);

        // 4. Cleanup function to remove listeners on unmount
        return () => {
            scroller.removeEventListener('mousedown', onMouseDown);
            scroller.removeEventListener('mouseleave', onMouseLeave);
            scroller.removeEventListener('mouseup', onMouseUp);
            scroller.removeEventListener('mousemove', onMouseMove);
        };
    }, [loadingVisible]); // 🚨 Add loadingVisible to the dependency array
    const handleChange = (_event, newValue) => {
        setValue(newValue);
    };

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = "/verify";
    };

    const drawerOptions = [
        {
            icon: <PendingActionsOutlinedIcon />,
            text: "سوابق تراکنش",
            link: "/transaction",
        },
        {
            icon: <ShoppingBasketOutlinedIcon />,
            text: "سفارشات",
            link: "/order",
        },
        {
            icon: <ReduceCapacityOutlinedIcon />,
            text: "درباره ما",
            link: "/about-us",
        },
        {
            icon: <PrivacyTipOutlinedIcon />,
            text: "قوانین و مقررات",
            link: "/privacy-policy",
        },
        {
            icon: <PrivacyTipOutlinedIcon />,
            text: "خروج از حساب کاربری",
            logout: true,
        },
    ];

    return (
        <>
            {/* Splash به صورت overlay */}
            <LoadingSplash ref={loadingRef} />

            {!loadingVisible && (
                <Box
                    sx={{
                        minHeight: "100vh",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* بخش اصلی صفحه (هدر + محتوا + منوی پایین) */}
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <AppBar
                            position="static"
                            sx={{ background: "#ffffffcc", boxShadow: "none" }}
                        >
                            <ToolbarMui>
                                {userData ? (
                                    <IconButton
                                        edge="start"
                                        onClick={handleDrawerOpen}
                                        size="large"
                                    >
                                        <MenuIcon sx={{ color: "#000" }} />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        edge="start"
                                        component={Link}
                                        to="/verify"
                                        size="large"
                                        sx={{
                                            color: "#000",
                                            fontSize: 14,
                                        }}
                                    >
                                        ورود
                                    </IconButton>
                                )}

                                <ListItemText align="center">
                                    <strong
                                        style={{
                                            width: "90%",
                                            fontSize: 18,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            color: "black",
                                        }}
                                    >
                                        Hamekara
                                    </strong>
                                </ListItemText>

                                <IconButton component={Link} to="/search" size="large">
                                    <SearchIcon sx={{ color: "#000" }} />
                                </IconButton>

                                <IconButton size="large">
                                    <BasketComponent />
                                </IconButton>
                            </ToolbarMui>

                            {/* Tabs */}
                            <Tabs
                                ref={tabsRef}
                                onChange={handleChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                textColor="inherit"
                                TabIndicatorProps={{
                                    style: { display: "none" },
                                }}
                                sx={{
                                    direction: "rtl",
                                    "& .MuiTabs-scroller": {
                                        direction: "ltr",
                                        overflowX: "auto !important",
                                        overflowY: "hidden",
                                        whiteSpace: "nowrap",
                                        WebkitOverflowScrolling: "touch",
                                        touchAction: "pan-x",
                                        overscrollBehaviorX: "contain",
                                        cursor: "grab", // Add this line
                                    },
                                    "& .MuiTabs-flexContainer": {
                                        flexWrap: "nowrap",
                                        justifyContent: "flex-start",
                                        paddingLeft: "0 !important",
                                        paddingRight: "0 !important",
                                        marginLeft: "0 !important",
                                        marginRight: "0 !important",
                                    },
                                    "& .MuiTab-root": {
                                        minWidth: "auto !important",
                                        paddingLeft: "12px",
                                        paddingRight: "12px",
                                        margin: "0 !important",
                                        color: "#000",
                                        fontWeight: 700,
                                    },
                                }}
                            >
                                {/* Tab 0: همه پذیرندگان */}
                                <Tab
                                    key="all"
                                    label="همه پذیرندگان"
                                    sx={{
                                        fontWeight: value === 0 ? 900 : "bold",
                                        direction: "rtl",
                                        color: "#000",
                                        borderBottom:
                                            value === 0
                                                ? "3px solid #ff0033"
                                                : "3px solid transparent",
                                        pb: 0.5,
                                        m: "0 !important",
                                    }}
                                />

                                {/* دسته‌بندی‌ها: index + 1 */}
                                {fetchedCategories.map((category, index) => {
                                    const tabIndex = index + 1;
                                    const isActive = value === tabIndex;

                                    return (
                                        <Tab
                                            key={category.id}
                                            label={category.title}
                                            sx={{
                                                fontWeight: isActive ? 900 : "bold",
                                                direction: "rtl",
                                                color: "#000",
                                                borderBottom: isActive
                                                    ? "3px solid #ff0033"
                                                    : "3px solid transparent",
                                                pb: 0.5,
                                                m: "0 !important",
                                            }}
                                        />
                                    );
                                })}
                            </Tabs>
                        </AppBar>

                        {/* Drawer */}
                        <Drawer
                            sx={{
                                width: drawerWidth,
                                flexShrink: 0,
                                "& .MuiDrawer-paper": {
                                    width: drawerWidth,
                                    boxSizing: "border-box",
                                    direction: "rtl",
                                },
                            }}
                            variant="temporary"
                            anchor="left"
                            open={open}
                            onClose={handleDrawerClose}
                        >
                            <DrawerHeader>
                                <IconButton onClick={handleDrawerClose}>
                                    <ChevronLeftIcon />
                                </IconButton>
                            </DrawerHeader>
                            <Divider />

                            {userData && (
                                <Box sx={{ p: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item>
                                            <img
                                                src={
                                                    userData?.picture?.url
                                                        ? AVATAR_BASE_URL_API +
                                                        userData.picture.url
                                                        : AvatarIcon
                                                }
                                                alt="avatar"
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: "50%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight="bold"
                                                color="green"
                                            >
                                                {userData.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {userData.phone}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                موجودی: {userData.wallet}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            <Divider />

                            <List>
                                {drawerOptions.map((item, index) =>
                                    item.logout ? (
                                        <ListItem
                                            button
                                            key={index}
                                            onClick={handleLogout}
                                        >
                                            <ListItemIcon>{item.icon}</ListItemIcon>
                                            <ListItemText
                                                primary={item.text}
                                                sx={{ textAlign: "right" }}
                                            />
                                        </ListItem>
                                    ) : (
                                        <ListItem
                                            button
                                            key={index}
                                            component={Link}
                                            to={item.link}
                                            onClick={handleDrawerClose}
                                        >
                                            <ListItemIcon>{item.icon}</ListItemIcon>
                                            <ListItemText
                                                primary={item.text}
                                                sx={{ textAlign: "right" }}
                                            />
                                        </ListItem>
                                    )
                                )}
                            </List>
                        </Drawer>

                        {/* Banners */}
                        <Box sx={{ px: 2, pt: 1 }}>
                            <AliceCarousel
                                autoPlay
                                infinite
                                mouseTracking
                                disableDotsControls
                                disableButtonsControls
                                autoPlayInterval={2500}
                                animationDuration={900}
                            >
                                {banners.map((banner, i) => (
                                    <Box key={i} sx={{ px: 0.5 }}>
                                        <img
                                            src={BANNER_BASE_URL_API + banner.picture.url}
                                            alt=""
                                            style={{
                                                width: "100%",
                                                borderRadius: 12,
                                                objectFit: "cover",
                                            }}
                                        />
                                    </Box>
                                ))}
                            </AliceCarousel>
                        </Box>

                        {/* محتوا + رزرواسیون برای BottomMenu */}
                        <Box sx={{ pb: "80px" }}>
                            {/* تب همه پذیرندگان */}
                            {value === 0 && (
                                <TabPanel value={value} index={0}>
                                    <Suspense
                                        fallback={
                                            <div style={{ padding: 16 }}>
                                                در حال بارگذاری...
                                            </div>
                                        }
                                    >
                                        <LazyMarketComponent parent_category_id={-1} />
                                    </Suspense>
                                </TabPanel>
                            )}

                            {/* تب‌های دسته‌بندی */}
                            {fetchedCategories.map((category, index) => {
                                const tabIndex = index + 1;

                                if (value !== tabIndex) return null;

                                return (
                                    <TabPanel
                                        key={category.id}
                                        value={value}
                                        index={tabIndex}
                                    >
                                        <Suspense
                                            fallback={
                                                <div style={{ padding: 16 }}>
                                                    در حال بارگذاری...
                                                </div>
                                            }
                                        >
                                            <LazyMarketComponent
                                                parent_category_id={category.id}
                                            />
                                        </Suspense>
                                    </TabPanel>
                                );
                            })}
                        </Box>

                        {/* منوی پایین (احتمالاً fixed) */}
                        <BottomMenu />
                    </Box>

                    {/* فوتر چسبیده به پایین */}
                    <Box
                        component="footer"
                        sx={{
                            backgroundColor: "#1e1e1e",
                            color: "#f1f1f1",
                            px: 3,
                            py: 4,
                            mt: 6,
                            fontSize: 14,
                            pb: "70px",
                            textAlign: "center",
                        }}
                    >
                        <Grid container spacing={3} justifyContent="center">
                            <Grid item xs={12} sm={4}>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", mb: 1 }}
                                >
                                    درباره همه کارا
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1 }}
                                >
                                    سامانه فروشگاهی <strong>همه کارا</strong> راهکاری
                                    نوین برای مدیریت فروشگاه‌های آنلاین و آفلاین است.
                                    با امکانات ثبت سفارش، باشگاه مشتریان، گزارش‌گیری
                                    مالی و پشتیبانی.
                                </Typography>
                                <a
                                    href="https://intro.hamekara.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: "#4fc3f7",
                                        textDecoration: "none",
                                        fontWeight: "bold",
                                    }}
                                >
                                    معرفی کامل همه کارا
                                </a>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", mb: 1 }}
                                >
                                    تماس با ما
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 0.5 }}
                                >
                                    📞 0383-228-5853
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 0.5 }}
                                >
                                    📧 thetoptraders@gmail.com
                                </Typography>
                                <Typography variant="body2">
                                    📍 ایران - شهرکرد - ساختمان علم و فناوری پردیس -
                                    طبقه سوم - واحد 305
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sm={4}
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <div className="trust-badge">
                                    <a
                                        referrerPolicy="origin"
                                        target="_blank"
                                        href="https://trustseal.enamad.ir/?id=618220&Code=W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                                    >
                                        <img
                                            referrerPolicy="origin"
                                            src="https://trustseal.enamad.ir/logo.aspx?id=618220&Code=W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                                            alt=""
                                            style={{ cursor: "pointer" }}
                                            code="W86FGXOVhlD2RNWlNLWwxE5EVujECL5N"
                                        />
                                    </a>
                                </div>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3, bgcolor: "#555" }} />

                        <Typography variant="caption" sx={{ color: "#aaa" }}>
                            © {new Date().getFullYear()} Hamekara.com - کلیه حقوق
                            محفوظ است
                        </Typography>
                    </Box>
                </Box>
            )}
        </>
    );
}

export default ToolbarComponent;
